import { Form, Row, Col, DatePicker, Select } from 'antd';
import { FormSelect, FormNumber } from '../../../components/form/FormBasic';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useWatch } from 'antd/es/form/Form';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import dayjs, { Dayjs } from 'dayjs';
import { normalize } from '../../../utils/normalizeWords';
import { ProductUnitEnum } from '../../../enums/ProductUnitEnum';
import { ProductiveCycleEnum } from '../../../enums/ProductiveCycleEnum';
import { useSubmitIHMAPricingData } from '../../../api/hooks/useExternalDataQuery';
import { IHMAPricingData } from '../../../api/services/analysisService';
import { getMonthByIndex } from '../../../enums/MonthEnum'

interface IHMAPricingFormValues {
  presentation?: string;
  dateFrom?: Dayjs;
  dateTo?: Dayjs;
  unit?: string;
  amountPerUnit?: number;
  unitOfStrategicReserve?: string;
  amountOfStrategicReserve?: number;
  productiveCycle?: string;
  productiveCycleOther?: string;
}

export const IHMAPricingForm: React.FC = () => {
  const { t } = useTranslation(['analysisAndReports']);
  const [form] = Form.useForm<IHMAPricingFormValues>();
  const { successNotification, errorNotification } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [presentationOptions, setPresentationOptions] = useState<any[]>([]);
  const [loadingPresentations, setLoadingPresentations] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  const submitMutation = useSubmitIHMAPricingData();
  const formValues = useWatch([], form);

  // Watch form changes and validate
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmitDisabled(false))
      .catch(() => setIsSubmitDisabled(true));
  }, [form, formValues]);

  // Fetch presentations on component mount
  useEffect(() => {
    const fetchPresentations = async () => {
      try {
        setLoadingPresentations(true);
        const presentationsData = await referenceDataSyncService.getPresentations();
        const options = presentationsData
          .map((presentation: any) => ({
            label: presentation.name || 'Unknown',
            value: presentation.refId,
          }))
          .sort((a: any, b: any) => (a.label || '').localeCompare(b.label || ''));
        setPresentationOptions(options);
      } catch (error: any) {
        errorNotification(error?.message || t('failedToLoadPresentations'));
        console.error('Error fetching presentations:', error);
      } finally {
        setLoadingPresentations(false);
      }
    };
    fetchPresentations();
  }, [t, errorNotification]);

  const handleSubmit = async (values: IHMAPricingFormValues) => {
    try {
      setLoading(true);
      const presentationsData = await referenceDataSyncService.getPresentations();
      const selectedPresentation = presentationsData.find((presentation: any) => presentation.refId === values.presentation);

      if (!selectedPresentation) {
        errorNotification(t('presentationNotFound'));
        return;
      }

      const startDate = `${values.dateFrom?.format('YYYY')} ${getMonthByIndex(values.dateFrom?.month()!)}`;
      const endDate = `${values.dateTo?.format('YYYY')} ${getMonthByIndex(values.dateTo?.month()!)}`;

      const payload: IHMAPricingData = {
        productId: selectedPresentation.id,
        startDate,
        endDate,
        unit: values.unit!,
        amountPerUnit: values.amountPerUnit!,
        strategicReserveUnit: values.unitOfStrategicReserve!,
        strategicReserveAmount: values.amountOfStrategicReserve!,
        productiveCycle: values.productiveCycle!,
      };

      await submitMutation.mutateAsync(payload);
      successNotification(t('ihmaPricingSubmittedSuccess'));
      form.resetFields();
    } catch (error: any) {
      errorNotification(error?.message || t('submissionFailed'));
      console.error('IHMA pricing submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='mb-6'>
      <div className="mb-6 items-start">
        <h3 className="text-2xl font-semibold">{t('IHMAPricing')}</h3>
      </div>

      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        className="w-full border border-gray-300 rounded-lg p-6"
      >
        {/* Presentation Selection */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={'presentation'}
              label={t('presentation')}
              rules={[{ required: true, message: t('presentationRequired') }]}
            >
              <Select
                className="w-full"
                placeholder={t('presentationPlaceholder')}
                options={presentationOptions}
                showSearch
                disabled={loadingPresentations}
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Validity Period */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={'dateFrom'}
              label={t('startDate')}
              rules={[{ required: true, message: t('startDateRequired') }]}
            >
              <DatePicker
                picker="month"
                placeholder={t('startDate')}
                format="YYYY-MM"
                className="w-full"
                disabledDate={(current) => {
                  const endDate = form.getFieldValue('dateTo');
                  if (!endDate) return false;
                  return current.isAfter(endDate, 'month');
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name={'dateTo'}
              label={t('endDate')}
              rules={[
                { required: true, message: t('endDateRequired') },
                {
                  validator: (_, value) => {
                    const startDate = form.getFieldValue('dateFrom');
                    if (startDate && value && value.isBefore(startDate, 'month')) {
                      return Promise.reject(new Error(t('endDateMustBeAfterStartDate') || 'End Date must be after Start Date'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                picker="month"
                placeholder={t('endDate')}
                format="YYYY-MM"
                className="w-full"
                disabledDate={(current) => {
                  const startDate = form.getFieldValue('dateFrom');
                  if (!startDate) return false;
                  return current.isBefore(startDate, 'month');
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Unit and Amount */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormSelect
              name={'unit'}
              label={t('unit')}
              placeholder={t('selectUnit')}
              options={Object.values(ProductUnitEnum).map((unit) => ({
                label: unit,
                value: unit,
              }))}
              showSearch
              rules={[{ required: true, message: t('unitRequired') }]}
            />
          </Col>
          <Col xs={24} md={12}>
            <FormNumber
              name={'amountPerUnit'}
              label={t('amountPerUnit')}
              placeholder={t('enterAmountPerUnit')}
              type="float"
              precision={2}
              rules={[{ required: true, message: t('amountPerUnitRequired') }]}
            />
          </Col>
        </Row>

        {/* Unit of Strategic Reserve and Amount */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormSelect
              name={'unitOfStrategicReserve'}
              label={t('unitOfStrategicReserve')}
              placeholder={t('selectUnit')}
              options={Object.values(ProductUnitEnum).map((unit) => ({
                label: unit,
                value: unit,
              }))}
              showSearch
              rules={[{ required: true, message: t('unitOfStrategicReserveRequired') }]}
            />
          </Col>
          <Col xs={24} md={12}>
            <FormNumber
              name={'amountOfStrategicReserve'}
              label={t('amountOfStrategicReserve')}
              placeholder={t('enterAmount')}
              type="float"
              precision={2}
              rules={[{ required: true, message: t('amountOfStrategicReserveRequired') }]}
            />
          </Col>
        </Row>

        {/* Productive Cycle */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormSelect
              name={'productiveCycle'}
              label={t('productiveCycle')}
              placeholder={t('selectProductiveCycle')}
              options={Object.values(ProductiveCycleEnum).map((cycle) => ({
                label: t(cycle),
                value: cycle,
              }))}
              rules={[{ required: true, message: t('productiveCycleRequired') }]}
            />
          </Col>
        </Row>

        {/* Submit Button */}
        <Row gutter={[16, 16]} className="mt-8">
          <Col xs={24} className="flex justify-end">
            <PrimarySquareBtn
              handleOnClick={() => { }}
              disabled={isSubmitDisabled || loading}
            >
              {t('enter')}
            </PrimarySquareBtn>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default IHMAPricingForm;
